import { useState, useCallback, useMemo } from 'react';
import { track } from '@/lib/telemetry';
import { PageSkeleton } from '@/components/PageSkeleton';
import { useInvestmentHoldings } from '@/hooks/queries/useInvestmentHoldings';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState } from './ErrorState';
import type { InvestmentItem } from '@/types';

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseDueDate(dueDate: string): Date {
  const d = new Date(dueDate);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function InvestmentCalendarView() {
  const formatCurrency = useFormatCurrency();
  const { data: holdings, error, isLoading, mutate } = useInvestmentHoldings();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [month, setMonth] = useState<Date>(new Date());

  const handleRetry = useCallback(() => {
    void mutate();
  }, [mutate]);

  const maturityMap = useMemo<Map<string, InvestmentItem[]>>(() => {
    const map = new Map<string, InvestmentItem[]>();
    for (const inv of holdings ?? []) {
      if (!inv.dueDate) continue;
      const d = parseDueDate(inv.dueDate);
      const key = toDateKey(d);
      const list = map.get(key) ?? [];
      list.push(inv);
      map.set(key, list);
    }
    return map;
  }, [holdings]);

  const maturityDates = useMemo<Date[]>(() => {
    return Array.from(maturityMap.keys()).map((key) => {
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y, m - 1, d);
    });
  }, [maturityMap]);

  const selectedInvestments = useMemo<InvestmentItem[]>(() => {
    if (!selectedDate) return [];
    return maturityMap.get(toDateKey(selectedDate)) ?? [];
  }, [selectedDate, maturityMap]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error && !holdings) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro ao carregar investimentos'}
        onAction={handleRetry}
      />
    );
  }

  const today = new Date();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-text-primary sm:text-lg">Calendário de Vencimentos</h2>
        <p className="mt-0.5 text-sm text-text-secondary sm:text-xs">
          {maturityDates.length} {maturityDates.length === 1 ? 'vencimento registrado' : 'vencimentos registrados'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="flex justify-center p-4 sm:p-5">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              // Re-clicking the selected day deselects (date === undefined) —
              // that transition is a real interaction and counts too.
              if (date?.getTime() !== selectedDate?.getTime()) {
                track('ui.period_changed', { surface: 'investments_calendar', method: 'day_select' });
              }
              setSelectedDate(date);
            }}
            month={month}
            onMonthChange={(next) => {
              track('ui.period_changed', { surface: 'investments_calendar', method: 'month_step' });
              setMonth(next);
            }}
            modifiers={{ hasDueDate: maturityDates }}
            modifiersClassNames={{
              // Brand marks the day, not success — a maturity is information, not a
              // positive outcome (§01.9).
              hasDueDate: 'bg-brand-50 text-brand-700 font-bold rounded-full',
            }}
          />
        </Card>

        <Card className="p-4 sm:p-5">
          {selectedDate ? (
            <>
              <h3 className="mb-3 text-sm font-semibold text-text-primary">
                {selectedDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </h3>
              {selectedInvestments.length > 0 ? (
                <div className="space-y-2">
                  {selectedInvestments.map((inv) => {
                    const daysUntil = Math.ceil((parseDueDate(inv.dueDate!).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={inv.id} className="flex items-start gap-3 rounded-xl bg-surface-sunken p-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-text-primary sm:text-xs">{inv.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            {inv.subtype && (
                              <Badge variant="default" size="sm">
                                {inv.subtype}
                              </Badge>
                            )}
                            <Badge variant="default" size="sm">
                              {inv.type}
                            </Badge>
                          </div>
                          <p className="mt-1 font-mono text-sm text-text-secondary tabular-nums sm:text-2xs">{formatCurrency(inv.balance)}</p>
                        </div>
                        <Badge
                          variant={daysUntil > 0 ? 'primary' : daysUntil === 0 ? 'warning' : 'error'}
                          size="sm"
                          className="shrink-0"
                        >
                          {daysUntil > 0 ? `em ${daysUntil}d` : daysUntil === 0 ? 'hoje' : `${Math.abs(daysUntil)}d atrás`}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-text-secondary sm:text-xs">Nenhum vencimento neste dia</p>
              )}
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center py-8 text-center">
              <p className="text-base font-medium text-text-secondary sm:text-sm">Selecione um dia no calendário</p>
              <p className="mt-1 text-sm text-text-tertiary sm:text-xs">Dias marcados possuem investimentos com vencimento</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
